/**
 * QUIPWA — a small reserve of Drive file ids for offline creation
 *
 * `files.generateIds` hands out ids before the files exist. Keeping a few in
 * the device DB lets a note created offline be born with its final Drive id,
 * and makes its first upload idempotent (a retried create with the same id
 * cannot duplicate the file). When the reserve is empty the note gets a
 * `local-…` id instead, which works just as well (see note-model.ts).
 *
 * Refilled by the notes sync while online: one request, only when low.
 */
import type { KvStore } from '../local/kv-store'
import { KV } from '../local/kv-store'

export interface IdSource {
  generateIds(count: number): Promise<string[]>
}

export const ID_POOL_TARGET = 20
export const ID_POOL_LOW_WATER = 8

export class DriveIdPool {
  private readonly kv: KvStore
  /** Serialises take/refill: both read-modify-write the same kv entry. */
  private chain: Promise<unknown> = Promise.resolve()

  constructor(kv: KvStore) {
    this.kv = kv
  }

  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.chain.then(fn, fn)
    this.chain = next.catch(() => undefined)
    return next
  }

  /** One reserved id, or null when the reserve is empty. */
  take(): Promise<string | null> {
    return this.serial(async () => {
      const ids = (await this.kv.get<string[]>(KV.idPool)) ?? []
      const id = ids.shift() ?? null
      if (id) await this.kv.set(KV.idPool, ids)
      return id
    })
  }

  size(): Promise<number> {
    return this.serial(async () => ((await this.kv.get<string[]>(KV.idPool)) ?? []).length)
  }

  /** Top the reserve up to `ID_POOL_TARGET` when it has fallen below the low-water mark. */
  refill(source: IdSource): Promise<number> {
    return this.serial(async () => {
      const ids = (await this.kv.get<string[]>(KV.idPool)) ?? []
      if (ids.length >= ID_POOL_LOW_WATER) return 0
      const fresh = await source.generateIds(ID_POOL_TARGET - ids.length)
      await this.kv.set(KV.idPool, [...ids, ...fresh])
      return fresh.length
    })
  }
}
